/**
 * LoginPage — dual login (ADR-003 D8), capability-adaptive (SIH-17 / DEF-B4-1).
 *
 * Tab rendering depends on the runtime detection of the customer auth gateway
 * (authStore.customerGatewayAvailable):
 *   - capability = `present` → dual tabs (Customer default, per ADR-003 D8
 *     customer-host users outnumber operator sessions)
 *   - capability = `absent`  → Employee (Support) tab only, Employee default,
 *     with the PD-43 unavailable notice. No Customer form or tab — the rule is
 *     capability-gated, not CSS-hidden.
 *     D53 changes presentation, not this capability contract.
 *
 * The capability-`absent` state is the deep-link contract: any path that
 * could land the user on the login page on a gateway-less host (anonymous
 * session on ncv parked, on a future host without a gateway, on a
 * misconfigured host) sees the unavailable state — never a fake login
 * form, never a placeholder success.
 *
 * Two tabs (when capability is `present`):
 *   - Customer → username+password → POST /api/auth/login on the per-host
 *     gateway. Single step.
 *   - Employee → username+password → POST /api/staff-auth/login on the BFF,
 *     then an OTP input → POST /api/staff-auth/verify (TicketApp SSO contract;
 *     the BFF is the only TicketApp caller per ADR-001).
 *
 * Dumb renderer: maps LoginFailureReason → user-facing copy and renders the
 * form. All flow control lives in authStore. Per ADR-003 §1.2 QAS-2 the
 * credential form does NOT leak whether the username exists (generic 401).
 *
 * Real states (per design README §6 — every state first-class):
 *   - submitting  → submit button disabled, button label flips to "Signing in…"
 *   - failed      → inline error under the form, form remains editable
 *   - default     → form is interactive, no error copy shown
 *
 * Routing on success is handled by AuthGate — LoginPage does not navigate.
 */

import { useState, type FormEvent } from 'react'
import { useAuthStore } from '@stores/authStore'
import { type LoginFailureReason, type StepReason, CREDENTIAL_FAILED_COPY, STEP_REASON_COPY, isStepReason, CUSTOMER_SIGN_IN_UNAVAILABLE, STAFF_SIGN_IN_GUIDANCE } from '@domain/auth'

interface ReasonCopy {
  readonly title: string
  readonly detail: string
}

const REASON_COPY: Record<Exclude<LoginFailureReason, StepReason>, ReasonCopy> = {
  validation: {
    title: 'Please check your input',
    detail: 'Username and password are both required.',
  },
  invalidCredentials: {
    title: CREDENTIAL_FAILED_COPY.title,
    detail: CREDENTIAL_FAILED_COPY.detail,
  },
  locked: {
    title: 'Account locked',
    detail: 'Account is temporarily locked. Try again later.',
  },
  rateLimited: {
    title: 'Too many attempts',
    detail: 'Too many login attempts. Wait a moment and try again.',
  },
  unavailable: {
    title: 'Service unavailable',
    detail: 'The auth backend cannot be reached. Check your network and try again.',
  },
}

export function LoginPage(): JSX.Element {
  const status = useAuthStore((s) => s.status)
  const authKind = useAuthStore((s) => s.authKind)
  const lastFailureReason = useAuthStore((s) => s.lastFailureReason)
  const employeeStep = useAuthStore((s) => s.employeeStep)
  const pendingDisplayName = useAuthStore((s) => s.pendingDisplayName)
  const customerGatewayAvailable = useAuthStore((s) => s.customerGatewayAvailable)
  const customerLoginAction = useAuthStore((s) => s.customerLogin)
  const employeeLoginAction = useAuthStore((s) => s.employeeLogin)
  const employeeVerifyAction = useAuthStore((s) => s.employeeVerify)
  const cancelEmployeeLoginAction = useAuthStore((s) => s.cancelEmployeeLogin)

  // Capability-driven default tab (SIH-17 / DEF-B4-1). Customer is the default
  // when the customer gateway is verified present (ADR-003 D8). When the
  // capability probe reports absent, Employee is the default AND the only
  // rendered tab — there is no Customer tab to select.
  const customerLoginAvailable = customerGatewayAvailable === 'present'
  const [activeTab, setActiveTab] = useState<'customer' | 'employee'>(
    customerLoginAvailable ? 'customer' : 'employee',
  )
  // If the resolved capability disagrees with the initial default (e.g. the
  // user kept the page mounted while a probe settled), keep the active tab
  // consistent — switch to Employee when the Customer tab becomes unreachable.
  // This guard does not re-render Customer on its own; the JSX below is the
  // source of truth.
  const effectiveTab: 'customer' | 'employee' =
    activeTab === 'customer' && !customerLoginAvailable ? 'employee' : activeTab
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)

  const isSubmitting = status === 'authenticating'
  // The store clears lastFailureReason on the next status change, but the
  // local error mirror gives us a stable "what just failed" without coupling
  // the page to the store's transient state.
  const visibleReason: LoginFailureReason | null =
    status === 'failed' && lastFailureReason !== null && authKind !== null ? lastFailureReason : null

  // When the employee flow advances to its OTP step, the store drives the
  // page — independent of which tab the user clicked. Render the OTP form
  // whenever the store says we are in the OTP step AND the session is not
  // "anonymous" (which would imply the user already backed out). `awaiting_otp`
  // is the steady-state between employee login's 202 and the user's OTP
  // submit; `authenticating` covers the in-flight verify call; `failed` covers
  // a rejected verify where the SPA keeps the pendingLoginRef so the user
  // can retry without re-entering credentials.
  const showOtpStep = authKind === 'employee' && employeeStep === 'otp' && status !== 'anonymous'

  async function handleCredentialsSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setLocalError(null)
    const trimmedUser = username.trim()
    if (trimmedUser === '' || password === '') {
      setLocalError('Username and password are both required.')
      return
    }
    try {
      // effectiveTab is the resolved source of truth; an activeTab='customer'
      // with capability='absent' would otherwise route through the dead
      // customer login action.
      if (effectiveTab === 'customer') {
        await customerLoginAction(trimmedUser, password)
      } else {
        // Employee step 1 → returns OTP prompt; the page re-renders the OTP form.
        await employeeLoginAction(trimmedUser, password)
      }
    } catch {
      // Every failure is swallowed here. On a genuine failure the store
      // records the failure state (status + lastFailureReason) and the
      // error copy renders from `visibleReason` below; on abort the store
      // restores its previous snapshot instead.
      // (CUSTOMER_GATEWAY_UNAVAILABLE) carry no lastFailureReason, so the
      // local error mirror handles them — they never appeared to begin
      // with because the Customer tab is absent. Nothing follows this
      // try/catch, so falling through is the whole behaviour (TDL row 88:
      // the former abort early-return was dead in effect and removed).
    }
  }

  async function handleOtpSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setLocalError(null)
    if (otp.trim() === '') {
      setLocalError('Enter the verification code we sent to your phone.')
      return
    }
    try {
      await employeeVerifyAction(otp)
      // AuthGate re-renders the dashboard on success.
    } catch {
      // Every failure is swallowed here. On a genuine failure the store
      // records the failure state; on abort it restores its previous
      // snapshot instead, so a cancelled verify is not a failure. Nothing
      // (TDL row 88: the former abort early-return was dead in effect and
      // removed).
    }
  }

  function switchTab(tab: 'customer' | 'employee'): void {
    setActiveTab(tab)
    setUsername('')
    setPassword('')
    setOtp('')
    setLocalError(null)
  }

  const reasonCopy = visibleReason === null ? null : isStepReason(visibleReason) ? STEP_REASON_COPY[visibleReason] : REASON_COPY[visibleReason]

  return (
    // D53: public identity bar, then one bounded sign-in context and form.
    // The authenticated AppHeader (identity, refresh, navigation) stays private.
    <div className="login-page" data-testid="login-page">
      <header className="app-header">
        <span className="app-title">Smart Invoice Health</span>
      </header>
      <main className="login-main" data-testid="login-main">
        <div className="login-card" data-testid="login-card">
          <div className="dash-hero login-hero">
            <h1 className="login-title" data-testid="login-title">Sign in</h1>
            <p className="login-subtitle">Sign in to view invoice sync status.</p>
          </div>
          <div className="login-card-body">

        {!showOtpStep ? (
          <div className="period-segmented login-tabs" role="tablist" aria-label="Sign-in method" data-testid="login-tabs">
            {customerLoginAvailable ? (
            <button
              type="button"
              role="tab"
              aria-selected={effectiveTab === 'customer'}
              className={effectiveTab === 'customer' ? 'segment login-tab login-tab-active' : 'segment login-tab'}
              onClick={() => { switchTab('customer') }}
              data-testid="login-tab-customer"
            >
              Customer
            </button>
            ) : null}
            <button
              type="button"
              role="tab"
              aria-selected={effectiveTab === 'employee'}
              className={effectiveTab === 'employee' ? 'segment login-tab login-tab-active' : 'segment login-tab'}
              onClick={() => { switchTab('employee') }}
              data-testid="login-tab-employee"
            >
              Employee (Support)
            </button>
          </div>
        ) : null}

        {!showOtpStep && !customerLoginAvailable ? (
          <div className="login-unavailable" role="status" data-testid="login-customer-unavailable">
            <strong>{CUSTOMER_SIGN_IN_UNAVAILABLE}</strong>
            <span>{STAFF_SIGN_IN_GUIDANCE}</span>
          </div>
        ) : null}

        {!showOtpStep ? (
          <form
            className="login-form"
            onSubmit={(event) => { void handleCredentialsSubmit(event) }}
            data-testid="login-form"
            noValidate
          >
            <label className="login-field">
              <span>Username</span>
              <input
                type="text"
                name="username"
                autoComplete="username"
                autoCapitalize="off"
                spellCheck={false}
                disabled={isSubmitting}
                value={username}
                onChange={(event) => { setUsername(event.target.value) }}
                data-testid="login-username"
              />
            </label>

            <label className="login-field">
              <span>Password</span>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                disabled={isSubmitting}
                value={password}
                onChange={(event) => { setPassword(event.target.value) }}
                data-testid="login-password"
              />
            </label>

            <button
              type="submit"
              className="login-submit"
              disabled={isSubmitting}
              data-testid="login-submit"
            >
              {isSubmitting ? 'Signing in…' : 'Continue'}
            </button>
          </form>
        ) : (
          <form
            className="login-form login-form-otp"
            onSubmit={(event) => { void handleOtpSubmit(event) }}
            data-testid="login-otp-form"
            noValidate
          >
            <p className="login-otp-prompt" data-testid="login-otp-prompt">
              {pendingDisplayName === null ? 'Enter the verification code we sent to your phone.' : `Hi ${pendingDisplayName} — enter the verification code we sent to your phone.`}
            </p>

            <label className="login-field">
              <span>Verification code</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                disabled={isSubmitting}
                value={otp}
                onChange={(event) => { setOtp(event.target.value) }}
                data-testid="login-otp"
              />
            </label>

            <button
              type="submit"
              className="login-submit"
              disabled={isSubmitting}
              data-testid="login-otp-submit"
            >
              {isSubmitting ? 'Verifying…' : 'Verify'}
            </button>

            <button
              type="button"
              className="login-secondary"
              onClick={() => { cancelEmployeeLoginAction() }}
              data-testid="login-otp-cancel"
            >
              Use different credentials
            </button>
          </form>
        )}

        {reasonCopy !== null ? (
          <div className="login-error" role="alert" data-testid="login-error">
            <strong>{reasonCopy.title}</strong>{' '}
            <span>{reasonCopy.detail}</span>
          </div>
        ) : null}

        {localError !== null ? (
          <div className="login-error" role="alert" data-testid="login-local-error">
            <strong>Please check your input</strong>
            <span>{localError}</span>
          </div>
        ) : null}
          </div>
        </div>
      </main>
    </div>
  )
}
